import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInFull as MaxIcon,
  Minimize as MinIcon,
} from '@mui/icons-material';

const getYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const getYouTubeStartTime = (url: string): number | null => {
  const tMatch = url.match(/[?&]t=([^&\n?#]+)/i);
  if (!tMatch || !tMatch[1]) return null;
  const timeString = tMatch[1].toLowerCase();
  if (/^\d+$/.test(timeString)) return parseInt(timeString, 10);
  if (timeString.endsWith('s') && /^\d+s?$/.test(timeString)) {
    return parseInt(timeString.replace('s', ''), 10);
  }
  let totalSeconds = 0;
  const hoursMatch = timeString.match(/(\d+)h/);
  if (hoursMatch) totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
  const minutesMatch = timeString.match(/(\d+)m/);
  if (minutesMatch) totalSeconds += parseInt(minutesMatch[1], 10) * 60;
  const secondsMatch = timeString.match(/(\d+)s(?!\d)/);
  if (secondsMatch) totalSeconds += parseInt(secondsMatch[1], 10);
  return hoursMatch || minutesMatch || secondsMatch ? totalSeconds : null;
};

const isYouTubeUrl = (url: string): boolean => /youtube\.com|youtu\.be/.test(url);

const isDirectVideoUrl = (url: string): boolean =>
  /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(url);

export type FloatingVideoSize = 'minimized' | 'normal' | 'maximized';

interface FloatingVideoPlayerProps {
  open: boolean;
  url: string;
  title?: string;
  onClose: () => void;
}

const TOOLBAR_HEIGHT = 40;
const SIZE_MAP = {
  minimized: { width: 280, height: 0 },
  normal: { width: 400, height: 225 },
  maximized: { width: 640, height: 360 },
};

/**
 * Bottom-right floating video player (YouTube-style PiP).
 * Video starts playing immediately when opened. Uses HTML5 <video> for direct
 * files and YouTube iframe for YouTube URLs.
 */
const FloatingVideoPlayer: React.FC<FloatingVideoPlayerProps> = ({
  open,
  url,
  title = 'Video',
  onClose,
}) => {
  const [size, setSize] = useState<FloatingVideoSize>('normal');
  const trimmedUrl = (url || '').trim();

  useEffect(() => {
    if (!open) setSize('normal');
  }, [open]);

  if (!open || !trimmedUrl) return null;

  const { width, height: videoHeight } = SIZE_MAP[size];
  const totalHeight = TOOLBAR_HEIGHT + videoHeight;

  const renderVideo = () => {
    if (isYouTubeUrl(trimmedUrl)) {
      const videoId = getYouTubeVideoId(trimmedUrl);
      if (!videoId) return null;
      const startTime = getYouTubeStartTime(trimmedUrl);
      let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      if (startTime != null && startTime > 0) embedUrl += `&start=${startTime}`;
      return (
        <iframe
          title={title}
          src={embedUrl}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      );
    }
    if (isDirectVideoUrl(trimmedUrl)) {
      return (
        <video
          autoPlay
          controls
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        >
          <source src={trimmedUrl} type="video/mp4" />
        </video>
      );
    }
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Cannot play this format.{' '}
          <a href={trimmedUrl} target="_blank" rel="noopener noreferrer">
            Open in new tab
          </a>
        </Typography>
      </Box>
    );
  };

  const showVideo = size !== 'minimized';

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width,
        minHeight: size === 'minimized' ? TOOLBAR_HEIGHT : totalHeight,
        zIndex: (theme) => theme.zIndex.modal + 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#000',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          bgcolor: 'rgba(0,0,0,0.85)',
          color: '#fff',
          minHeight: 40,
        }}
      >
        <Typography variant="body2" noWrap sx={{ flex: 1, mr: 0.5, fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Box>
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.9)' }}
            onClick={() => setSize(s => (s === 'minimized' ? 'normal' : 'minimized'))}
            title={size === 'minimized' ? 'Expand' : 'Minimize'}
          >
            <MinIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.9)' }}
            onClick={() =>
              setSize(s => (s === 'maximized' ? 'normal' : 'maximized'))
            }
            title={size === 'maximized' ? 'Restore' : 'Maximize'}
          >
            <MaxIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.9)' }}
            onClick={onClose}
            title="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {showVideo && videoHeight > 0 && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: videoHeight,
            overflow: 'hidden',
            bgcolor: '#000',
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0 }}>
            {renderVideo()}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default FloatingVideoPlayer;
